import { DeleteOutlined } from "@ant-design/icons";
import { useNotification } from "@refinedev/core";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Typography,
} from "antd";
import { ColumnType } from "antd/es/table";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  useCreateBankAccount,
  useDeleteBankAccount,
  useUpdateBankAccount,
} from "../../../../api/bankAccount";
import { useBankAccountManagement } from "../../../../utils/hooks/useBankAccountManagement";
import { useIsAdminRole } from "../../../../utils/hooks/useIdentityAdmin.hook";
import { ButtonListHeader } from "../../../components/buttonsList";
import { Row as RowTable } from "../../../components/interface";
import FormBank from "../../../customers/edit/components/beneficiaries-detail/form-bank";

interface ICompanyBankAccounts {
  tabActive: "info" | "bank" | "wallet";
  id: string | undefined;
  userId: number | undefined;
  isActive?: boolean;
}

const DEFAULT_START = 0;
const DEFAULT_PAGE_SIZE = 10;

const CompanyBankAccounts = ({
  tabActive,
  id,
  isActive = true,
  userId,
}: ICompanyBankAccounts) => {
  const [searchForm] = Form.useForm();
  const [bankForm] = Form.useForm();
  const { open: openNotification } = useNotification();
  const isAdminRole = useIsAdminRole();
  const [actionBankAccount, setActionBankAccount] = useState<
    "ADD" | "EDIT" | null
  >(null);
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [deleteBankAccountId, setDeleteBankAccountId] = useState<number | null>(
    null
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState({
    start: DEFAULT_START,
    end: DEFAULT_START + DEFAULT_PAGE_SIZE,
  });
  const [searchName, setSearchName] = useState<string>("");

  const { dataListBankAccount, refetch, isLoading } = useBankAccountManagement(
    Number(id || 0),
    params,
    searchName,
    Boolean(isActive && id)
  );

  const moveToAvailablePageAfterDelete = useCallback(async () => {
    const result = await refetch();
    const responseData: any = result.data;
    const total = Number(responseData?.totalCount ?? 0);
    const pageSize = Number(responseData?.pageSize ?? DEFAULT_PAGE_SIZE);
    const maxPage = Math.max(1, Math.ceil(total / pageSize));

    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
      setParams({
        start: (maxPage - 1) * pageSize,
        end: maxPage * pageSize,
      });
    }
  }, [currentPage, refetch]);

  useEffect(() => {
    if (isActive && id) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, id]);

  // Reset pagination when search changes
  useEffect(() => {
    if (searchName !== undefined) {
      setCurrentPage(1);
      setParams({
        start: DEFAULT_START,
        end: DEFAULT_START + DEFAULT_PAGE_SIZE,
      });
    }
  }, [searchName]);

  const { mutate: createBankAccount, isLoading: isLoadingCreate } =
    useCreateBankAccount({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification?.({
            key: "add_bank_account_success",
            message:
              response.data.message || "Bank account created successfully!",
            type: "success",
          });
        }
        refetch();
        setActionBankAccount(null);
        setBankAccountId(null);
        bankForm.resetFields();
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "add_bank_account_failure",
            message: error?.message || "Failed to create bank account!",
            type: "error",
          });
        }
      },
    });

  const { mutate: updateBankAccount, isLoading: isLoadingUpdate } =
    useUpdateBankAccount({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification?.({
            key: "update_bank_account_success",
            message:
              response.data.message || "Bank account updated successfully!",
            type: "success",
          });
        }
        refetch();
        setActionBankAccount(null);
        setBankAccountId(null);
        // bankForm.resetFields();
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "update_bank_account_failure",
            message: error?.message || "Failed to update bank account!",
            type: "error",
          });
        }
      },
    });

  const { mutate: deleteBankAccount, isLoading: loadingDeleteWallet } =
    useDeleteBankAccount({
      onSuccess: async (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification?.({
            key: "delete_bank_account_success",
            message:
              response.data.message || "Delete bank account successfully!",
            type: "success",
          });
        }
        await moveToAvailablePageAfterDelete();
        setDeleteBankAccountId(null);
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "delete_bank_accountt_failure",
            message: error?.message || "Delete bank account failed!",
            type: "error",
          });
        }
      },
    });

  const handlePageChange = (page: number, pageSize?: number) => {
    const resolvedPageSize = pageSize || DEFAULT_PAGE_SIZE;
    setCurrentPage(page);
    setParams({
      start: (page - 1) * resolvedPageSize,
      end: page * resolvedPageSize,
    });
  };

  const handleSearch = (values: { searchValue?: string }) => {
    const trimmedSearchValue = values.searchValue?.trim() || "";
    setSearchName(trimmedSearchValue);
  };

  useEffect(() => {
    searchForm.setFieldsValue({ searchValue: searchName });
  }, [searchName, searchForm]);

  const handleCancel = () => {
    if (isLoadingCreate || isLoadingUpdate) return;
    setActionBankAccount(null);
    setBankAccountId(null);
    if (actionBankAccount === "ADD") {
      bankForm.resetFields();
    }
  };

  const handleCancelDelete = () => {
    if (loadingDeleteWallet) return;
    setDeleteBankAccountId(null);
  };

  const handleSubmitDelete = () => {
    if (deleteBankAccountId) {
      deleteBankAccount(deleteBankAccountId);
    }
  };

  const onSubmitBankAccount = (values: any) => {
    const bank = !values?.bankId
      ? {
        name: values?.bankName || "",
        swift: values?.bankSwift || "",
        legalAddress: values?.bankAddress || "",
        country: values?.bankCountry || "",
      }
      : undefined;

    const body = {
      ...(values?.bankId ? { bankId: values?.bankId } : { bank }),
      currencyCode: values?.currencyCode || "",
      abaRoutingNumber: values?.abaRoutingNumber || undefined,
      bankStateBranchNumber: values?.bankStateBranchNumber || undefined,
      branchNumber: values?.branchNumber || "",
      accountNumber: values?.accountNumber || "",
      companyId: Number(id),
      userId: Number(userId),
    };

    if (actionBankAccount === "ADD") {
      createBankAccount(body as any);
    } else {
      if (!body) return;
      updateBankAccount({
        id: bankAccountId,
        ...body,
      } as any);
    }
  };

  const columns = useMemo((): ColumnType<RowTable>[] => {
    const columns: ColumnType<RowTable>[] = [
      {
        dataIndex: "id",
        title: <span className={"column-header"}>NO.</span>,
        render: (_value, _record, index) => {
          return (
            <Typography className="text-table">
              {(currentPage - 1) * DEFAULT_PAGE_SIZE +
                index +
                1}
            </Typography>
          );
        },
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        width: "6%",
      },
      {
        dataIndex: "accountNumber",
        title: <span className={"column-header"}>ACCT NO</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        width: "8.2%",
      },
      {
        dataIndex: "currencyCode",
        title: <span className={"column-header"}>CURRENCY</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        width: "8.2%",
      },
      {
        dataIndex: "red",
        title: <span className={"column-header"}>BANK</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        render: (_value, data: any) => {
          return (
            <Typography className="text-table">{data.bank?.name}</Typography>
          );
        },
        width: "31.6%",
      },
      {
        dataIndex: "correspondentBankSwift",
        title: <span className={"column-header"}>SWIFT</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        render: (value, data: any) => {
          return (
            <Typography className="text-table">{data.bank?.swift}</Typography>
          );
        },
        width: "14.4%",
      },
      {
        dataIndex: "country",
        title: <span className={"column-header"}>BANK ADDRESS</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        render: (value, data: any) => {
          return (
            <Typography className="text-table">
              {data.bank?.legalAddress}
            </Typography>
          );
        },
        width: "20.4%",
      },
      {
        dataIndex: "actions",
        title: "",
        render: (_value, record: RowTable) =>
          isAdminRole && (
            <Button
              className="list-button list-button--delete"
              icon={<DeleteOutlined style={{ fontSize: "19px" }} />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteBankAccountId(record.id as number);
              }}
            />
          ),
        width: "8%",
      },
    ];
    return columns.filter(({ key }) => !key);
  }, [currentPage, isAdminRole]);

  return (
    <div style={{ display: tabActive === "bank" ? "block" : "none" }}>
      <Typography.Title level={5}>Bank Accounts</Typography.Title>
      <Form
        form={searchForm}
        layout="vertical"
        onFinish={handleSearch}
        style={{
          background: "#FAFAFA",
          borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
          padding: "16px 10px 0",
        }}
      >
        <Row justify="space-between" align="middle">
          <Col lg={8} md={10}>
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item
                name="searchValue"
                style={{ width: "100%" }}
                initialValue={searchName}
              >
                <Input
                  placeholder="Search by ACCT NO"
                  allowClear
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    if (!value || value.trim() === "") {
                      searchForm.submit();
                    }
                  }}
                  onPressEnter={() => searchForm.submit()}
                />
              </Form.Item>
              <Button
                type="primary"
                style={{ borderRadius: "0px 6px 6px 0px", marginTop: "5px" }}
                onClick={() => searchForm.submit()}
              >
                Search
              </Button>
            </Space.Compact>
          </Col>
          {isAdminRole && (
            <Col>
              {ButtonListHeader(() => {
                setActionBankAccount("ADD");
                setBankAccountId(null);
                bankForm.resetFields();
              })}
            </Col>
          )}
        </Row>
      </Form>

      <Table
        rowKey="id"
        columns={columns as any}
        dataSource={dataListBankAccount.items}
        loading={isLoading}
        pagination={{
          current: currentPage,
          total: dataListBankAccount.total,
          pageSize: DEFAULT_PAGE_SIZE,
          onChange: (page) => handlePageChange(page, DEFAULT_PAGE_SIZE),
        }}
      />
      <Modal
        open={!!actionBankAccount}
        onCancel={handleCancel}
        title={
          <>
            <div
              style={{
                marginBottom: "0px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {actionBankAccount === "ADD"
                ? "Add Bank Account"
                : "Edit Bank Account"}
            </div>
            {actionBankAccount === "EDIT" && bankAccountId && (
              <div
                style={{
                  marginBottom: "0px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                #{bankAccountId}
              </div>
            )}
          </>
        }
        className="edit-wallet-modal"
        width={"600px"}
        footer={null}
      >
        <FormBank
          form={bankForm}
          bankAccountId={bankAccountId}
          actionBankAccount={actionBankAccount}
          handleCancel={handleCancel}
          onSubmitBankAccount={onSubmitBankAccount}
          isLoading={isLoadingCreate || isLoadingUpdate}
          userId={userId}
          notGetUserBank={true}
        />
      </Modal>
      <Modal
        open={!!deleteBankAccountId}
        title="Confirm Delete"
        className="smartModalSM"
        onOk={handleSubmitDelete}
        onCancel={handleCancelDelete}
        confirmLoading={loadingDeleteWallet}
      >
        <p>Do you want to delete this record?</p>
      </Modal>
    </div>
  );
};

export { CompanyBankAccounts };
export default CompanyBankAccounts;
